import { useState } from 'react';
import {
  CoinDistributionChart,
  TransactionTypesChart,
  WalletGrowthChart,
} from '@/pages/gamification/components';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import { Activity, Download, TrendingUp, Users } from 'lucide-react';
import { toast } from 'sonner';
import {
  CoinDistributionPoint,
  WalletAnalytics,
  walletService,
} from '@/services/wallet.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type DateRangeOption = '7' | '30' | '90';

/**
 * Wallet Analytics Page
 * Comprehensive analytics dashboard with charts and metrics
 */
export function WalletAnalyticsPage() {
  const [dateRange, setDateRange] = useState<DateRangeOption>('30');

  // Calculate date range
  const getDateRange = () => {
    const days = parseInt(dateRange);
    const to = new Date();
    const from = subDays(to, days);
    return {
      from: format(from, "yyyy-MM-dd'T'00:00:00"),
      to: format(to, "yyyy-MM-dd'T'23:59:59"),
    };
  };

  // Fetch wallet analytics
  const {
    data: analytics,
    isLoading,
    error,
  } = useQuery<WalletAnalytics>({
    queryKey: ['wallet-analytics', dateRange],
    queryFn: () => walletService.getWalletAnalytics(getDateRange()),
    refetchInterval: 60000, // Refresh every minute
    retry: false, // Don't retry if endpoint doesn't exist
  });

  // Fetch overview metrics for coin distribution
  const { data: overview, error: overviewError } = useQuery({
    queryKey: ['wallet-overview', dateRange],
    queryFn: () => walletService.getOverviewMetrics(getDateRange()),
    retry: false, // Don't retry if endpoint doesn't exist
  });

  // Export analytics report
  const handleExport = () => {
    if (!analytics) return;

    // Create CSV content
    const csvContent = [
      ['Wallet Analytics Report'],
      ['Generated:', new Date().toISOString()],
      ['Date Range:', `Last ${dateRange} days`],
      [''],
      ['Summary Metrics'],
      [
        'Total Coins in Circulation',
        analytics.totalCoinsInCirculation.toLocaleString(),
      ],
      [
        'Coins Distributed Today',
        analytics.coinsDistributedToday.toLocaleString(),
      ],
      [
        'Coins Distributed This Week',
        analytics.coinsDistributedThisWeek.toLocaleString(),
      ],
      [
        'Coins Distributed This Month',
        analytics.coinsDistributedThisMonth.toLocaleString(),
      ],
      [''],
      ['Top Wallets'],
      ['Rank', 'User', 'Balance'],
      ...analytics.topWallets.map((wallet, idx) => [
        (idx + 1).toString(),
        wallet.userName,
        wallet.balance.toLocaleString(),
      ]),
      [''],
      ['Transaction Volume'],
      ['Date', 'Volume', 'Transactions'],
      ...analytics.transactionVolumeChart.map((point) => [
        point.date,
        point.volume.toLocaleString(),
        point.transactions.toLocaleString(),
      ]),
      [''],
      ['Distribution by Segment'],
      ['Segment', 'Total Coins', 'User Count'],
      ...analytics.distributionBySegment.map((segment) => [
        segment.segment,
        segment.totalCoins.toLocaleString(),
        segment.userCount.toLocaleString(),
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wallet-analytics-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success('Analytics report exported');
  };

  // Prepare chart data
  const coinDistributionData: CoinDistributionPoint[] =
    overview?.coinDistribution || [];

  const walletGrowthData =
    analytics?.transactionVolumeChart.map((point) => ({
      date: point.date,
      activeWallets: Math.floor(point.transactions / 5), // Estimate active wallets
      totalWallets: Math.floor(point.transactions / 3), // Estimate total wallets
    })) || [];

  const transactionTypesData =
    analytics?.distributionBySegment.map((segment) => ({
      category: segment.segment,
      count: segment.userCount,
      amount: segment.totalCoins,
    })) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <div className="text-center">
          <Activity className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Show error state if endpoints are not available
  if (error || overviewError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Wallet Analytics</h1>
          <p className="text-muted-foreground mt-1">
            Comprehensive insights into wallet performance and coin distribution
          </p>
        </div>
        <Card>
          <CardContent className="p-12">
            <div className="text-center">
              <Activity className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-semibold mb-2">
                Analytics Not Available
              </h3>
              <p className="text-muted-foreground mb-4">
                The analytics endpoints are not yet implemented on the backend.
              </p>
              <p className="text-sm text-muted-foreground">
                Please implement the following endpoints in the Wallet service:
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>
                  <code className="bg-muted px-2 py-1 rounded">
                    /api/wallet/analytics/wallet-stats
                  </code>
                </li>
                <li>
                  <code className="bg-muted px-2 py-1 rounded">
                    /api/wallet/analytics/overview
                  </code>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Wallet Analytics</h1>
          <p className="text-muted-foreground mt-1">
            Comprehensive insights into wallet performance and coin distribution
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={dateRange}
            onValueChange={(value) => setDateRange(value as DateRangeOption)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleExport} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Coins</CardTitle>
            <img
              src="/T-Spark-Icon.svg"
              alt="T-Spark"
              className="h-4 w-4 dark:invert"
            />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.totalCoinsInCirculation.toLocaleString() || '0'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">In circulation</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Today</CardTitle>
            <TrendingUp className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.coinsDistributedToday.toLocaleString() || '0'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Coins distributed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
            <TrendingUp className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.coinsDistributedThisWeek.toLocaleString() || '0'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Coins distributed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <TrendingUp className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.coinsDistributedThisMonth.toLocaleString() || '0'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Coins distributed
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Coin Distribution Chart */}
        <div className="lg:col-span-2">
          <CoinDistributionChart
            data={coinDistributionData}
            title="Coin Distribution Over Time"
            height={400}
          />
        </div>

        {/* Wallet Growth Chart */}
        <WalletGrowthChart
          data={walletGrowthData}
          title="Wallet Activity Trend"
          height={350}
        />

        {/* Transaction Types Chart */}
        <TransactionTypesChart
          data={transactionTypesData}
          title="Distribution by Segment"
          height={350}
          showBy="amount"
        />
      </div>

      {/* Top Wallets Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Top 10 Wallets
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics?.topWallets.slice(0, 10).map((wallet, index) => (
              <div
                key={wallet.userId}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                      index === 0
                        ? 'bg-yellow-500 text-white'
                        : index === 1
                          ? 'bg-gray-400 text-white'
                          : index === 2
                            ? 'bg-orange-600 text-white'
                            : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium">{wallet.userName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <img
                    src="/T-Spark-Icon.svg"
                    alt="T-Spark"
                    className="h-4 w-4 dark:invert"
                  />
                  <span className="font-bold">
                    {wallet.balance.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Transaction Volume Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Recent Transaction Volume
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {analytics?.transactionVolumeChart
              .slice(-7)
              .reverse()
              .map((point) => (
                <div
                  key={point.date}
                  className="flex items-center justify-between"
                >
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(point.date), 'MMM dd, yyyy')}
                  </span>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {point.volume.toLocaleString()} coins
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {point.transactions.toLocaleString()} transactions
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
